/*
 * Finding an instrument among about a hundred thousand.
 *
 * The search is debounced, because a query per keystroke against a table that
 * size is work nobody asked for. 220ms is roughly the gap between characters
 * when typing a symbol you already know, so in practice one query is sent per
 * symbol rather than one per letter.
 *
 * Results are keyed by the Zerodha instrument token, not by the symbol —
 * RELIANCE exists on NSE and on BSE and they are different instruments with
 * different prices.
 */

import { useEffect, useRef, useState } from "react";

import { useInstrumentSearch } from "../api/hooks";
import type { Instrument } from "../api/types";
import { IconSearch } from "./Icons";

interface Props {
  onPick: (instrument: Instrument) => void;
  /** Already on the strategy — shown as such rather than offered again. */
  exclude?: number[];
  placeholder?: string;
  autoFocus?: boolean;
}

export function SymbolPicker({ onPick, exclude = [], placeholder, autoFocus }: Props) {
  const [typed, setTyped] = useState("");
  const [query, setQuery] = useState("");
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setQuery(typed.trim()), 220);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [typed]);

  const results = useInstrumentSearch(query);
  const rows = results.data ?? [];
  const has = new Set(exclude);

  return (
    <div className="col" style={{ gap: 8 }}>
      <div style={{ position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--text-faint)",
            pointerEvents: "none",
            display: "grid",
          }}
        >
          <IconSearch size={15} />
        </span>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={placeholder ?? "Search by symbol or company name"}
          style={{ paddingLeft: 32 }}
          autoFocus={autoFocus}
          autoComplete="off"
          aria-label="Search instruments"
        />
      </div>

      {query && (
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {results.isPending ? (
            <div className="empty" style={{ padding: 18 }}>
              <span className="spinner" />
            </div>
          ) : rows.length === 0 ? (
            <div className="empty" style={{ padding: 18 }}>
              Nothing matched “{query}”.
              <div className="tiny faint mt">
                If the list looks empty everywhere, refresh the instrument list in
                Settings — it is fetched from Zerodha once a day.
              </div>
            </div>
          ) : (
            rows.map((i) => {
              const already = has.has(i.instrumentToken);
              return (
                <button
                  key={i.instrumentToken}
                  disabled={already}
                  onClick={() => {
                    onPick(i);
                    setTyped("");
                    setQuery("");
                  }}
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 11px",
                    background: "none",
                    border: "none",
                    borderBottom: "1px solid var(--border)",
                    cursor: already ? "default" : "pointer",
                    opacity: already ? 0.45 : 1,
                    textAlign: "left",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{i.tradingsymbol}</div>
                    <div
                      className="tiny faint"
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {i.name || "—"}
                    </div>
                  </div>
                  <span className="pill">{i.exchange}</span>
                  {already && <span className="tiny faint">added</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
